import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BuyerNavigationLayoutContext,
  useBuyerNavigationLayoutContext,
} from '@/enterprise/components/BuyerNavigationLayout';
import OfferDetailsBanner from '@/enterprise/components/OfferDetailsBanner/OfferDetailsBanner';
import TakerMarketStatsGrid from '@/enterprise/features/takerMarketStatsGrid/TakerMarketStatsGrid';
import LinkTabs from '@/shared/components/LinkTabs';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import EarlyMarketProcessingBanner from './EarlyMarketProcessingBanner';
import NewDivisionAlert, { useGetNewDivisionAlertData } from './NewDivisionAlert';

const BuyerInvoiceLayout = () => {
  const { t } = useTranslation();
  const { buyer, loader, takerMarket, urlParams } = useBuyerNavigationLayoutContext();
  const navigate = useNavigate();
  const { loadingKey } = loader;
  const { takerId } = urlParams;
  const linkItems = [
    {
      key: 'eligible',
      label: t('core.eligible'),
      link: 'eligible',
      matcher: 'eligible',
    },
    {
      key: 'ineligible',
      label: t('core.ineligible'),
      link: 'ineligible',
      matcher: 'ineligible',
    },
    {
      key: 'adjustments',
      label: t('maker.adjustments'),
      link: 'adjustments',
      matcher: 'adjustments',
    },
    {
      key: 'awarded',
      label: t('taker.awarded'),
      link: 'awarded',
      matcher: 'awarded',
    },
  ];

  // Navigate to awarded invoices tab if manually awarded so that the supplier can see what invoices will
  // be awarded
  useEffect(() => {
    if (takerMarket.offerConfig.isManuallyAwarded) {
      navigate(
        asEnterpriseLink(
          `customers/${takerMarket.makerOrganizationUuid}/markets/${takerMarket.marketUuid}/division/${takerMarket.takerDivisionId}/invoices/awarded`
        )
      );
    }
  }, [
    navigate,
    takerMarket.offerConfig.isManuallyAwarded,
    takerMarket.makerOrganizationUuid,
    takerMarket.marketUuid,
    takerMarket.takerDivisionId,
  ]);

  const { takerMarketsWithRate } = useGetNewDivisionAlertData([takerMarket]);

  const content = (
    <>
      <div className="space-y-6 px-4 py-6">
        {takerMarketsWithRate.length > 0 ? (
          <NewDivisionAlert takerMarkets={buyer.takerMarkets} referrer="division" />
        ) : (
          <OfferDetailsBanner takerMarket={takerMarket} />
        )}
        <TakerMarketStatsGrid key={loadingKey} takerId={takerId} takerMarket={takerMarket} />
      </div>
      <div>
        <div className="border-b border-deepGray-500/[12%] px-4">
          <LinkTabs items={linkItems} variant="secondary" />
        </div>
        <Outlet
          context={
            {
              buyer,
              loader,
              takerMarket,
              urlParams,
            } satisfies BuyerNavigationLayoutContext
          }
        />
      </div>
    </>
  );

  return takerMarket.offerConfig.isManuallyAwarded || takerMarket.offerConfig.isActiveExpressAccept ? (
    <>
      <div className="p-4 pb-0">
        <EarlyMarketProcessingBanner takerMarket={takerMarket} />
      </div>
      {/* Adds an overlay to disable interaction with the content.
      pointer-events-none disables all pointer events, including cursor changes.
      So adding separate absolute overlay div with cursor-not-allowed that sits on top  */}
      <div className="relative">
        <div className="pointer-events-none opacity-50">{content}</div>
        <div className="absolute inset-0 cursor-not-allowed" />
      </div>
    </>
  ) : (
    content
  );
};

export default BuyerInvoiceLayout;
